// Performance Dashboard Page - Admin only - SLA metrics and team performance
import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminHeader from '../../../shared/components/AdminHeader';
import DashboardCard from '../components/DashboardCard';

interface PerformanceDashboardProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
}

// Mock SLA overview data
const mockOverview = {
  period: 'weekly',
  totalItemsReceived: 97,
  totalItemsCompleted: 88,
  totalItemsInProgress: 9,
  totalItemsUnassigned: 4,
  avgTimeToFirstResponse: 1800, // 30 minutes in seconds
  avgTimeToResolution: 14400, // 4 hours in seconds
  dueDateComplianceRate: 87.9,
  breachedItems: 2,
  atRiskItems: 3,
};

// Mock operator performance data
const mockOperatorPerformance = [
  {
    operator: { id: '1', displayName: 'Operator One', status: 'available' },
    workload: { current: 3, max: 10, utilizationRate: 30 },
    metrics: {
      itemsCompleted: 42,
      avgTimeToResolution: 9000, // 2.5h
      dueDateComplianceRate: 93.3,
      formattedResolution: '2.5h',
    },
  },
  {
    operator: { id: '2', displayName: 'Operator Two', status: 'busy' },
    workload: { current: 6, max: 8, utilizationRate: 75 },
    metrics: {
      itemsCompleted: 38,
      avgTimeToResolution: 11520, // 3.2h
      dueDateComplianceRate: 82.6,
      formattedResolution: '3.2h',
    },
  },
  {
    operator: { id: '3', displayName: 'Admin User', status: 'available' },
    workload: { current: 2, max: 15, utilizationRate: 13 },
    metrics: {
      itemsCompleted: 55,
      avgTimeToResolution: 6480, // 1.8h
      dueDateComplianceRate: 98.2,
      formattedResolution: '1.8h',
    },
  },
];

// Mock SLA trends
const mockTrends = [
  { periodStart: '2024-01-25', itemsCompleted: 45, dueDateComplianceRate: 89, avgResolution: '3.1h' },
  { periodStart: '2024-01-26', itemsCompleted: 52, dueDateComplianceRate: 87, avgResolution: '3.4h' },
  { periodStart: '2024-01-27', itemsCompleted: 48, dueDateComplianceRate: 91, avgResolution: '2.9h' },
  { periodStart: '2024-01-28', itemsCompleted: 61, dueDateComplianceRate: 94, avgResolution: '2.5h' },
  { periodStart: '2024-01-29', itemsCompleted: 55, dueDateComplianceRate: 88, avgResolution: '3.0h' },
  { periodStart: '2024-01-30', itemsCompleted: 42, dueDateComplianceRate: 86, avgResolution: '3.2h' },
  { periodStart: '2024-01-31', itemsCompleted: 38, dueDateComplianceRate: 92, avgResolution: '2.7h' },
];

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ brand, Icons, onNavigate }) => {
  const [period, setPeriod] = useState('weekly');

  const getComplianceColor = (rate) => {
    if (rate >= 95) return '#10B981';
    if (rate >= 85) return '#F59E0B';
    return '#EF4444';
  };

  const statusColors = {
    available: '#10B981',
    busy: '#F59E0B',
    away: '#6B7280',
    offline: '#EF4444',
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader
        title="Performance Dashboard"
        subtitle="SLA metrics and team performance analytics"
        brand={brand}
        Icons={Icons}
        rightContent={
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        }
      />

      <div className="flex-1 overflow-auto p-6">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <DashboardCard
            icon={Icons.Document}
            label="Received"
            count={mockOverview.totalItemsReceived}
            variant="primary"
            size="large"
            brand={brand}
          />
          <DashboardCard
            icon={Icons.Check}
            label="Completed"
            count={mockOverview.totalItemsCompleted}
            variant="secondary"
            size="large"
            brand={brand}
          />
          <DashboardCard
            icon={Icons.Clock}
            label="In Progress"
            count={mockOverview.totalItemsInProgress}
            variant="primary"
            size="large"
            brand={brand}
          />
          <DashboardCard
            icon={Icons.Briefcase}
            label="Unassigned"
            count={mockOverview.totalItemsUnassigned}
            alerts={mockOverview.totalItemsUnassigned > 0 ? 1 : 0}
            variant="secondary"
            size="large"
            brand={brand}
          />
          <DashboardCard
            icon={Icons.Info}
            label="At Risk"
            count={mockOverview.atRiskItems}
            alerts={mockOverview.atRiskItems}
            variant="secondary"
            size="large"
            brand={brand}
          />
          <DashboardCard
            icon={Icons.X}
            label="Breached"
            count={mockOverview.breachedItems}
            alerts={mockOverview.breachedItems}
            variant="secondary"
            size="large"
            brand={brand}
          />
        </div>

        {/* SLA Performance Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* First Response Time */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
                Avg First Response
              </h3>
              <span
                className="text-xs px-2 py-0.5 rounded-sm"
                style={{ backgroundColor: '#D1FAE5', color: '#059669' }}
              >
                On Target
              </span>
            </div>
            <div className="text-3xl font-bold mb-1" style={{ color: brand.colors.text }}>
              {formatDuration(mockOverview.avgTimeToFirstResponse)}
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Target: 30m
            </div>
          </div>

          {/* Resolution Time */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
                Avg Resolution Time
              </h3>
              <span
                className="text-xs px-2 py-0.5 rounded-sm"
                style={{ backgroundColor: '#D1FAE5', color: '#059669' }}
              >
                On Target
              </span>
            </div>
            <div className="text-3xl font-bold mb-1" style={{ color: brand.colors.text }}>
              {formatDuration(mockOverview.avgTimeToResolution)}
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Target: 4h
            </div>
          </div>

          {/* Due Date Compliance */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
                SLA Compliance
              </h3>
              <span
                className="text-xs px-2 py-0.5 rounded-sm"
                style={{
                  backgroundColor: mockOverview.dueDateComplianceRate >= 95 ? '#D1FAE5' : '#FEF3C7',
                  color: mockOverview.dueDateComplianceRate >= 95 ? '#059669' : '#D97706',
                }}
              >
                {mockOverview.dueDateComplianceRate >= 95 ? 'On Target' : 'Below Target'}
              </span>
            </div>
            <div
              className="text-3xl font-bold mb-1"
              style={{ color: getComplianceColor(mockOverview.dueDateComplianceRate) }}
            >
              {mockOverview.dueDateComplianceRate.toFixed(1)}%
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Target: 95%
            </div>
          </div>
        </div>

        {/* Operator Performance Table */}
        <div
          className="rounded-xl p-5 mb-6"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
              Operator Performance
            </h3>
            <button
              onClick={() => onNavigate('team')}
              className="text-sm hover:underline"
              style={{ color: brand.colors.link }}
            >
              View Team
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Operator</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Status</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Workload</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Completed</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Avg Resolution</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Compliance</th>
                </tr>
              </thead>
              <tbody>
                {mockOperatorPerformance.map((item, index) => (
                  <tr
                    key={item.operator.id}
                    style={{ borderBottom: index < mockOperatorPerformance.length - 1 ? `1px solid ${brand.colors.border}` : 'none' }}
                  >
                    <td className="py-3 px-3">
                      <span className="font-medium" style={{ color: brand.colors.text }}>
                        {item.operator.displayName}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: statusColors[item.operator.status] }}
                        />
                        <span className="text-sm capitalize" style={{ color: brand.colors.textLight }}>
                          {item.operator.status}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${item.workload.utilizationRate}%`,
                              backgroundColor: item.workload.utilizationRate >= 80 ? '#F59E0B' : '#10B981',
                            }}
                          />
                        </div>
                        <span className="text-sm" style={{ color: brand.colors.textLight }}>
                          {item.workload.current}/{item.workload.max}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className="font-medium" style={{ color: brand.colors.text }}>
                        {item.metrics.itemsCompleted}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm" style={{ color: brand.colors.textLight }}>
                        {item.metrics.formattedResolution}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className="font-medium"
                        style={{ color: getComplianceColor(item.metrics.dueDateComplianceRate) }}
                      >
                        {item.metrics.dueDateComplianceRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Trends */}
        <div
          className="rounded-xl p-5"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <h3 className="font-semibold mb-4" style={{ color: brand.colors.secondary }}>
            7-Day Trend
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Date</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Completed</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Compliance</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Avg Resolution</th>
                </tr>
              </thead>
              <tbody>
                {mockTrends.map((trend, index) => (
                  <tr
                    key={trend.periodStart}
                    style={{ borderBottom: index < mockTrends.length - 1 ? `1px solid ${brand.colors.border}` : 'none' }}
                  >
                    <td className="py-2 px-3">
                      <span className="text-sm" style={{ color: brand.colors.text }}>
                        {new Date(trend.periodStart).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="font-medium" style={{ color: brand.colors.text }}>
                        {trend.itemsCompleted}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className="font-medium"
                        style={{ color: getComplianceColor(trend.dueDateComplianceRate) }}
                      >
                        {trend.dueDateComplianceRate}%
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-sm" style={{ color: brand.colors.textLight }}>
                        {trend.avgResolution}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
